import React, { useState, useEffect } from "react";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";

const RCSCallbackURLModal = ({
  show,
  handleClose,
  customerData,
  fetchCustomerData,
}) => {
  const [callbackUrl, setCallbackUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loggedInUserName = sessionStorage.getItem("username");

  // ✅ SET EXISTING CALLBACK URL FROM PARENT DATA
  useEffect(() => {
    if (show && customerData) {
      setCallbackUrl(customerData?.callbackUrl || "");
    }
  }, [show, customerData]);

  const validateUrl = (url) => {
    // OPTIONAL FIELD
    if (!url.trim()) return "";

    const urlPattern =
      /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

    if (!urlPattern.test(url)) {
      return "Enter a valid URL";
    }

    return "";
  };

  const handleSubmit = async () => {
    const validationError = validateUrl(callbackUrl);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const requestBody = {
        username: loggedInUserName,
        customerCode: customerData?.customerCode || "",
        callbackUrl: callbackUrl,
      };

      const response = await axiosPostApi(
        apiList.RCS_CALLBACK_ADD_UPDATE,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        CustomAlert({
          title: "Success",
          text:
            response?.data?.message || "RCS Callback URL updated successfully!",
          icon: "success",
          confirmButtonText: "Ok",
        });

        handleClose();
        setCallbackUrl("");

        if (fetchCustomerData && typeof fetchCustomerData === "function") {
          fetchCustomerData();
        }
      } else if (response?.data?.respCode === "05") {
        CustomAlert({
          title: "Info",
          text: response?.data?.message || "Invalid request. Please try again!",
          icon: "info",
          confirmButtonText: "Retry",
        });
      } else {
        CustomAlert({
          title: "Error",
          text: response?.data?.message || "Failed to update URL",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (err) {
      console.error("Error updating callback URL:", err);
      CustomAlert({
        title: "Error",
        text: "Something went wrong. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setCallbackUrl("");
    setError("");
    handleClose();
  };

  if (!show) return null;

  return (
    // 🔥 HIGHER Z-INDEX OVER EVERYTHING
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]">
      {/* Modal Box */}
      <div className="bg-white w-[90%] max-w-md rounded-xl p-6 shadow-lg relative z-[10000]">
        {/* Header */}
        <h2 className="text-lg font-semibold text-gray-800 mb-6 text-center">
          RCS Callback URL
        </h2>

        {/* Input Field */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Enter Callback URL
          </label>
          <input
            type="url"
            placeholder="https://your-domain.com/callback"
            value={callbackUrl}
            onChange={(e) => {
              setCallbackUrl(e.target.value);
              if (error) setError("");
            }}
            className={`w-full px-3 py-2 border rounded-md text-sm outline-none focus:ring-2 ${
              error
                ? "border-red-500 focus:ring-red-200"
                : "border-gray-300 focus:ring-teal-200"
            }`}
          />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>

        {/* Buttons */}
        <div className="flex justify-center gap-3 mt-6">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-50 transition-colors duration-200"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-md text-white bg-teal-500 hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors duration-200"
          >
            {loading
              ? "Submitting..."
              : customerData?.callbackUrl
              ? "Update"
              : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RCSCallbackURLModal;
